import { Building2, Eraser, KeyRound, LogIn, LogOut, MessagesSquare, Plus, ShieldCheck, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";

import { BrandMark } from "../components/BrandMark";
import { RecapitiConsole } from "./console/ComunicazioniConsole";
import { MessaggiAzienda } from "./console/MessaggiAzienda";
import { descriviProva, ProvaAzienda } from "./console/ProvaAzienda";
import { EmptyState } from "../components/EmptyState";
import { ErrorState } from "../components/ErrorState";
import { MODULE_LIST } from "../../shared/modules";
import { PasswordField } from "../components/PasswordField";
import type { RouterOutputs } from "../lib/trpc";
import { trpc } from "../lib/trpc";

type Company = RouterOutputs["platform"]["companies"][number];
type Credenziali = { titolo: string; email: string; password: string; nota: string };

/** Il cartellino in alto a destra: corto, il dettaglio sta nella sezione Prova. */
function statoLeggibile(access: Company["access"]) {
  if (access.status === "trial" && access.blocked) return "Prova scaduta";
  if (access.status === "trial" && access.trialEndsAt) return descriviProva(access).split(":")[0];
  return descriviProva(access);
}

/**
 * L'esito raccontato a parole. `unknown` non e un difetto: e il servizio di posta che non
 * ha ancora detto niente, e scriverlo cosi evita che sembri un guasto.
 */
function classeStato(access: Company["access"]) {
  if (access.blocked) return "console-stato console-stato--chiusa";
  if (access.status === "customer") return "console-stato console-stato--cliente";
  return "console-stato console-stato--prova";
}

/**
 * Console di piattaforma: la schermata da cui Cristian vende.
 *
 * Vive fuori dall'app: nessun menu, nessuna azienda, un accesso suo. Le credenziali generate
 * si mostrano **una volta sola**, perche non vengono conservate in chiaro da nessuna parte:
 * se si perdono si rigenerano, non si vanno a rileggere.
 */
export function ConsolePage() {
  const utils = trpc.useUtils();
  const sessione = trpc.platform.me.useQuery(undefined, { retry: false });
  const login = trpc.platform.login.useMutation({ onSuccess: () => void utils.platform.invalidate() });
  const logout = trpc.platform.logout.useMutation({ onSuccess: () => void utils.platform.invalidate() });
  const companies = trpc.platform.companies.useQuery(undefined, { enabled: Boolean(sessione.data) });
  const audit = trpc.platform.audit.useQuery({ limit: 20 }, { enabled: Boolean(sessione.data) });
  const createTrial = trpc.platform.createTrial.useMutation({ onSuccess: () => void utils.platform.invalidate() });
  const setSuspended = trpc.platform.setSuspended.useMutation({ onSuccess: () => void utils.platform.invalidate() });
  const setModule = trpc.platform.setModule.useMutation({ onSuccess: () => void utils.platform.invalidate() });
  const resetPassword = trpc.platform.resetPassword.useMutation({ onSuccess: () => void utils.platform.invalidate() });
  const cleanDemoData = trpc.platform.cleanDemoData.useMutation({ onSuccess: () => void utils.platform.invalidate() });
  // L'accesso di assistenza cambia il cookie dell'app: si esce dalla console e si entra
  // nell'app con un caricamento vero, non con una navigazione interna.
  const impersonate = trpc.platform.impersonate.useMutation({ onSuccess: () => window.location.assign("/") });
  const [apertaId, setApertaId] = useState<string | null>(null);
  const [nuova, setNuova] = useState(false);
  const [credenziali, setCredenziali] = useState<Credenziali | null>(null);
  const [pulizia, setPulizia] = useState<string | null>(null);
  // Il riquadro nasce in cima alla pagina, mentre il pulsante sta piu in basso sulla persona:
  // senza portarlo in vista sembra che il clic non abbia fatto niente, e si clicca di nuovo
  // bruciando la password appena generata.
  const riquadroCredenziali = useRef<HTMLElement>(null);
  const riquadroPulizia = useRef<HTMLElement>(null);
  useEffect(() => { if (credenziali) riquadroCredenziali.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }); }, [credenziali]);
  useEffect(() => { if (pulizia) riquadroPulizia.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }); }, [pulizia]);
  // Quale quadrato ha i messaggi aperti: uno solo per volta, come per le persone.
  const [messaggiId, setMessaggiId] = useState<string | null>(null);
  const people = trpc.platform.people.useQuery({ companyId: apertaId ?? "" }, { enabled: Boolean(apertaId) });
  // Qui restano soltanto gli errori senza azienda: si chiedono al server gia scremati per
  // tipo, perche una segnalazione scritta da una persona ha sempre la sua azienda e non
  // potrebbe mai finire in questo elenco.
  const segnali = trpc.platform.signals.useQuery(
    { source: "error", limit: 100 },
    { enabled: Boolean(sessione.data) },
  );
  // Solo quelli che nessun quadrato puo mostrare, perche non appartengono a nessuna azienda.
  const senzaAzienda = (segnali.data ?? []).filter((segnale) => !segnale.companyId);

  async function entra(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      await login.mutateAsync({ email: String(data.get("email")), password: String(data.get("password")) });
    } catch {
      // L'errore resta nella mutazione e la schermata lo mostra sotto al modulo.
    }
  }

  async function creaProva(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      const esito = await createTrial.mutateAsync({
        companyName: String(data.get("companyName")),
        ownerName: String(data.get("ownerName")),
        ownerEmail: String(data.get("ownerEmail")),
        withDemoData: data.get("withDemoData") === "on",
      });
      if (!esito) return;
      setCredenziali({
        titolo: `Prova creata per ${esito.companyName}`,
        email: esito.email,
        password: esito.password,
        nota: `Prova fino al ${new Date(esito.trialEndsAt).toLocaleDateString("it-IT")}. Consegna queste credenziali adesso: non si potranno rileggere.${esito.demoSeeded ? " L'azienda nasce arredata con dati di esempio: la squadra dimostrativa non ha password, se serve entrarci si genera una temporanea da Persone e password." : ""}`,
      });
      setNuova(false);
    } catch {
      // Idem: l'errore lo mostra il modulo.
    }
  }

  async function pulisciEsempi(azienda: Company) {
    const esito = await cleanDemoData.mutateAsync({ companyId: azienda.id });
    if (!esito) return;
    const tolti = Object.values(esito.removed).reduce((totale, quanti) => totale + quanti, 0);
    const conservati = esito.kept.length > 0
      ? ` Conservati ${esito.kept.length} elementi su cui si lavora gia: ${esito.kept.map((voce) => voce.name).join(", ")}.`
      : "";
    setPulizia(`Dati di esempio rimossi da ${azienda.name}: ${tolti} elementi, ${esito.photos} fotografie, ${esito.deactivatedPeople} persone dimostrative disattivate.${conservati}`);
  }

  async function rigenera(userId: string, nome: string) {
    const esito = await resetPassword.mutateAsync({ userId });
    if (!esito) return;
    setCredenziali({
      titolo: `Password temporanea per ${nome}`,
      email: esito.email,
      password: esito.password,
      nota: "Va cambiata al primo accesso. Consegnala adesso: non si potra rileggere.",
    });
  }

  if (sessione.isLoading) return <main className="splash"><BrandMark /><span className="loader" /><p>Apertura della console...</p></main>;

  if (!sessione.data) {
    return (
      <main className="console-login">
        <form className="invite-card" onSubmit={entra}>
          <span className="invite-card__icon"><ShieldCheck /></span>
          <p className="eyebrow">Area riservata</p>
          <h1>Console MANUTIO</h1>
          <p>Accesso di piattaforma, separato dagli account delle aziende.</p>
          <label><span>Email</span><input name="email" type="email" required autoComplete="username" /></label>
          <PasswordField label="Password" name="password" required autoComplete="current-password" />
          {login.error && <p className="form-error">{login.error.message}</p>}
          <button className={`button button--primary${login.isPending ? " button--saving" : ""}`}>
            {login.isPending ? "Accesso..." : "Entra nella console"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="page console-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Piattaforma</p>
          <h1>Console</h1>
          <p>Aziende, prove, licenze, moduli e assistenza. Ogni azione resta registrata.</p>
        </div>
        <div className="page-heading__actions">
          <button className="button button--primary" type="button" onClick={() => setNuova(!nuova)}><Plus size={19} /> Nuova azienda di prova</button>
          <button className="button button--ghost" type="button" onClick={() => logout.mutate()}><LogOut size={17} /> Esci</button>
        </div>
      </header>

      {credenziali && (
        <section ref={riquadroCredenziali} className="credenziali-card" role="status">
          <h2>{credenziali.titolo}</h2>
          <dl>
            <div><dt>Email</dt><dd>{credenziali.email}</dd></div>
            <div><dt>Password</dt><dd className="credenziali-card__password">{credenziali.password}</dd></div>
          </dl>
          <p>{credenziali.nota}</p>
          <button className="button button--outline" type="button" onClick={() => setCredenziali(null)}>Ho consegnato le credenziali</button>
        </section>
      )}

      {pulizia && (
        <section ref={riquadroPulizia} className="credenziali-card credenziali-card--pulizia" role="status">
          <h2>Dati di esempio</h2>
          <p>{pulizia}</p>
          <button className="button button--outline" type="button" onClick={() => setPulizia(null)}>Ho letto</button>
        </section>
      )}

      {nuova && (
        <form className="editor-card" onSubmit={creaProva}>
          <div className="editor-card__heading"><h2>Nuova azienda di prova</h2><p>Trenta giorni da oggi, con il titolare gia dentro e, se vuoi, la casa gia arredata.</p></div>
          <div className="form-grid">
            <label><span>Nome azienda</span><input name="companyName" required minLength={2} placeholder="Verde Jesolo" /></label>
            <label><span>Nome del titolare</span><input name="ownerName" required minLength={2} placeholder="Giovanni Rossi" /></label>
            <label className="form-grid__wide"><span>Email del titolare</span><input name="ownerEmail" type="email" required placeholder="giovanni@esempio.it" /></label>
          </div>
          <label className="check-control">
            <input type="checkbox" name="withDemoData" defaultChecked /> Con dati di esempio
          </label>
          <p className="form-hint">
            Strutture, squadra, catalogo, interventi di oggi, richieste materiali e fornitori dimostrativi,
            ancorati a oggi. Chi apre la prova capisce in dieci secondi che cosa fa il programma.
          </p>
          {createTrial.error && <p className="form-error">{createTrial.error.message}</p>}
          <div className="form-actions">
            <button className="button button--ghost" type="button" onClick={() => setNuova(false)}>Annulla</button>
            <button className={`button button--primary${createTrial.isPending ? " button--saving" : ""}`}>
              {createTrial.isPending ? "Creazione..." : "Crea la prova"}
            </button>
          </div>
        </form>
      )}

      <section className="section-block">
        <div className="section-heading"><div><p className="eyebrow">Portafoglio</p><h2>Le aziende</h2></div></div>
        {companies.isLoading && <p className="loading-copy">Caricamento aziende...</p>}
        {!companies.isLoading && (companies.data ?? []).length === 0 && (
          <EmptyState title="Nessuna azienda">Crea la prima azienda di prova per cominciare.</EmptyState>
        )}
        <ul className="console-list">
          {companies.data?.map((azienda) => (
            <li className="console-card" key={azienda.id}>
              <div className="console-card__head">
                <div>
                  <strong><Building2 size={17} aria-hidden="true" /> {azienda.name}</strong>
                  <small>{azienda.people} {azienda.people === 1 ? "persona" : "persone"} · dal {new Date(azienda.createdAt).toLocaleDateString("it-IT")}</small>
                </div>
                <span className={classeStato(azienda.access)}>{statoLeggibile(azienda.access)}</span>
              </div>
              <div className="console-card__moduli">
                {MODULE_LIST.map((descrittore) => {
                  const acceso = azienda.modules.find((modulo) => modulo.key === descrittore.key)?.active ?? false;
                  return (
                    <label className="check-control" key={descrittore.key}>
                      <input
                        type="checkbox"
                        checked={acceso}
                        onChange={(event) => setModule.mutate({ companyId: azienda.id, moduleKey: descrittore.key, active: event.target.checked })}
                      /> {descrittore.label}
                    </label>
                  );
                })}
              </div>
              {/* Tutti i comandi della prova stanno qui, compresi Diventa cliente e Riporta in prova. */}
              <ProvaAzienda azienda={azienda} />
              <div className="console-card__actions">
                {azienda.access.status === "suspended"
                  ? <button type="button" className="button button--outline" onClick={() => setSuspended.mutate({ companyId: azienda.id, suspended: false })}>Riattiva</button>
                  : <button type="button" className="button button--ghost" onClick={() => setSuspended.mutate({ companyId: azienda.id, suspended: true })}>Sospendi</button>}
                <button type="button" className="button button--outline" onClick={() => impersonate.mutate({ companyId: azienda.id })}>
                  <LogIn size={16} /> Entra a controllare
                </button>
                {azienda.demoRecords > 0 && (
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => {
                      const conferma = window.confirm(
                        `Togliere i dati di esempio da ${azienda.name}?\n\nSpariscono strutture, interventi, richieste, fornitori e catalogo dimostrativi. Quello su cui si e gia lavorato resta dov'e, e le persone dimostrative vengono disattivate, non cancellate.`,
                      );
                      if (conferma) void pulisciEsempi(azienda);
                    }}
                  >
                    <Eraser size={16} /> Pulisci dati di esempio
                  </button>
                )}
                <button
                  type="button"
                  className="button button--outline"
                  aria-expanded={messaggiId === azienda.id}
                  aria-label={azienda.pendingSignals > 0 ? `Messaggi, ${azienda.pendingSignals} da guardare` : "Messaggi"}
                  onClick={() => setMessaggiId(messaggiId === azienda.id ? null : azienda.id)}
                >
                  <MessagesSquare size={16} /> Messaggi
                  {/* Il numero si vede da chiuso: e tutto il senso dell'indicatore. */}
                  {azienda.pendingSignals > 0 && <span className="pastiglia-conto">{azienda.pendingSignals}</span>}
                </button>
                <button type="button" className="button button--outline" onClick={() => setApertaId(apertaId === azienda.id ? null : azienda.id)}>
                  <KeyRound size={16} /> Persone e password
                </button>
              </div>
              {messaggiId === azienda.id && <MessaggiAzienda azienda={azienda} />}
              {apertaId === azienda.id && (
                <ul className="console-people">
                  {people.data?.map((persona) => (
                    <li key={persona.userId}>
                      <span><strong>{persona.name}</strong><small>{persona.email} · {persona.role === "owner" ? "Titolare" : persona.role === "manager" ? "Responsabile" : "Collaboratore"}</small></span>
                      <button type="button" className="button button--ghost" onClick={() => void rigenera(persona.userId, persona.name)}>Password temporanea</button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        {(setSuspended.error || setModule.error || resetPassword.error || cleanDemoData.error || impersonate.error) && (
          <p className="form-error">
            {(setSuspended.error ?? setModule.error ?? resetPassword.error ?? cleanDemoData.error ?? impersonate.error)?.message}
          </p>
        )}
      </section>

      <RecapitiConsole />

      {/*
        Quello che ha un'azienda vive dentro il suo quadrato, qui sopra. Fuori resta soltanto
        cio che un quadrato non potrebbe mostrare: gli errori capitati **prima** che ci fosse
        un'azienda, tipicamente all'accesso, che sono anche i piu raccontati al telefono.
        Senza questo blocco quei segnali non avrebbero nessuna casa e sparirebbero.
      */}
      <section className="section-block" role="region" aria-label="Segnali senza un'azienda">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Segnali</p>
            <h2>Errori senza un'azienda</h2>
            <p className="section-heading__nota">
              Quello che si rompe prima che ci sia un'azienda, di solito all'accesso. Tutto il resto
              sta dentro il quadrato dell'azienda a cui appartiene.
            </p>
          </div>
        </div>

        {segnali.isLoading && <p className="loading-copy">Caricamento segnali...</p>}
        {/* Prima lo stato d'errore, poi quello vuoto: a lettura fallita non si sa se c'e
            qualcosa, e dire "non c'e niente" sarebbe una risposta inventata. */}
        {!segnali.isLoading && segnali.error && (
          <ErrorState cosa="i segnali" dettaglio={segnali.error.message} onRiprova={() => void segnali.refetch()} />
        )}
        {/* Il vuoto solo con una risposta in mano: fra un tentativo e l'altro la lettura non
            carica e non ha ancora fallito, e li "non si e rotto niente" sarebbe inventato. */}
        {segnali.isSuccess && !segnali.error && senzaAzienda.length === 0 && (
          <EmptyState title="Nessun errore fuori da un'azienda">
            Non si e rotto niente prima dell'accesso. Le segnalazioni delle aziende stanno nei loro quadrati.
          </EmptyState>
        )}
        <ul className="segnali-elenco">
          {senzaAzienda.map((segnale) => (
            <li className={`segnale segnale--${segnale.source}`} key={`${segnale.source}-${segnale.id}`}>
              <div className="segnale__testa">
                <TriangleAlert size={16} aria-hidden="true" />
                <strong>Fuori da un'azienda</strong>
                <span>Errore in {segnale.context}</span>
                <small>{new Date(segnale.occurredAt).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</small>
              </div>
              <p>{segnale.message}</p>
              {segnale.personName && <small>{segnale.personName}</small>}
            </li>
          ))}
        </ul>
      </section>

      <section className="section-block">
        <div className="section-heading"><div><p className="eyebrow">Tracce</p><h2>Ultime azioni</h2></div></div>
        <ul className="console-audit">
          {audit.data?.map((riga) => (
            <li key={riga.id}>
              <span>{new Date(riga.occurredAt).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              <strong>{riga.action}</strong>
              <small>{riga.adminName}</small>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
